<template>
  <v-flex>
    <v-layout row wrap>
      <v-flex xs12>
        <h2 class="primary--text pb-2">Schema</h2>
        <p>
          Add in your template code here.
        </p>

        <div class="schema-editor-shell">
          <edit-code-pane
            :code="schemaStr"
            language="yaml"
            height="100%"
            :schema-intellisense="true"
            :schema-completions="schemaCompletions"
            v-on:update-code="updateSchema">
          </edit-code-pane>
        </div>

        <v-flex xs12 mt-3>
          <v-btn color="tertiary" class="ma-0 ml-0" @click="dialogApplyCases = true">
            Update existing cases
          </v-btn>
          <v-tooltip top color="secondary">
            <v-icon style="vertical-align:middle" color="secondary" class="ml-2" slot="activator">help</v-icon>
            <span>Apply the current schema to existing cases.</span>
          </v-tooltip>
          <v-btn
            v-if="isAppAdmin"
            color="primary"
            class="ma-0 ml-3"
            dark
            @click="$emit('reindex-cases')">
            Reindex Cases
          </v-btn>
          <v-tooltip v-if="isAppAdmin" top color="secondary">
            <v-icon style="vertical-align:middle" color="secondary" class="ml-2" slot="activator">help</v-icon>
            <span>Rebuild the search index for cases of this template.</span>
          </v-tooltip>
          <v-dialog v-model="dialogApplyCases" max-width="500">
            <v-card class="pa-4">
              <v-card-title class="pa-0 pb-2">
                <h2>Upgrade Cases?</h2>
              </v-card-title>
              <v-card-text class="pa-0">
                <p>Caution, this will apply the current schema to all currently existing cases.</p>
              </v-card-text>
              <v-card-actions class="ma-0 pa-0 pt-1">
                <v-btn @click="updateExistingCases()" color="success">Yes, apply to all cases</v-btn>
                <v-btn @click="dialogApplyCases = false" color="error">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
      </v-flex>
      <v-flex xs12>
        <h2 class="primary--text pb-2 pt-2 mt-4">Unique Identifier</h2>
        <p>This field uniquely identifies each field on the case. For example an order number or social security number.</p>
      </v-flex>
      <v-flex xs12 sm6>
        <v-text-field  v-model="uniqueIdentifierField" box label="Unique identifier name"></v-text-field>
      </v-flex>
       <v-flex xs12>
        <h2 class="primary--text pb-2 pt-2">Case Summary Fields</h2>
        <p class="pb-1">Add or delete fields. You can drag and drop rows to re-order using the handle on the left.</p>
      </v-flex>
      <v-flex xs12>
        <draggable
          style="width: 100%; min-height: 10px;"
          v-model="displayFields"
          :ghost="true"
        >
          <SortableItem
          v-for="(displaySet, index) in displayFields"
          :index="index"
          :key="displaySet.id"
          :position="index"
          :item="displaySet"
          :fieldsets="fieldsets"
          :caseType="caseType"
          v-on:field-remove="removeDisplayField"
          v-on:field-update="updateDisplayField"/>
        </draggable>
        <v-btn class="ml-0" color="secondary" @click="addNewDisplayField()">Add a summary field</v-btn>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import EditCodePane from '@/components/shared/editCodePane.vue'
import DeleteDialog from "@/components/shared/deleteDialog"
import draggable from "vuedraggable"
import yaml from 'js-yaml'
import { mapState } from "vuex"

// Custom Item for SortableList - Automatically updates item & index via v-model
const SortableItem = {
  components: { DeleteDialog },
  props: {
    caseType: { type: Object, required: true },
    item: { type: Object, required: true },
    fieldsets: { type: Array, required: true }
  },
  template: `
   <li class="drag-drop"> 
          <v-layout row fluid>
             <v-icon
            class="reorder-icon d-block mr-1 pr-1"
            color="secondary"
            style="margin-top:16px; margin-left:-5px; cursor:grab"
          >swap_vert</v-icon>
              <v-flex xs12 sm6>
                <v-select
            :items="fieldsets"
            item-text="name"
            v-model="item.fieldset"
            label="Select a fieldset"
            dense box
            append-icon="keyboard_arrow_down"
            :class="{'pr-0': $vuetify.breakpoint.xsAndDown, 'pr-2': $vuetify.breakpoint.smAndUp}">
          </v-select>
              </v-flex>
              <v-flex xs12 sm6>
                 <v-select
            :items="fields"
            item-text="name"
            v-model="item.fieldName"
            label="Select a field"
            @change="update(item)"
            dense box
            append-icon="keyboard_arrow_down"
            :class="{'pl-0': $vuetify.breakpoint.xsAndDown, 'pl-2': $vuetify.breakpoint.smAndUp}">
          </v-select>
              </v-flex>
              <v-btn class="mt-2 pt-2" icon  @click="deleteDialog = true">
                <v-icon color="error">cancel</v-icon>
              </v-btn>
            </v-layout>
            <delete-dialog :isOpen="deleteDialog" itemType="field" v-on:delete-item="remove"></delete-dialog> 
              </li>
     `,
  data() {
    return {
      deleteDialog: false
    }
  },
  computed: {
    fields() {
      let fieldset = {}
      fieldset = this.fieldsets.find((currentFieldset) => (currentFieldset.name === this.item.fieldset))
      if (fieldset && fieldset.fields) {
        return fieldset.fields
      } else {
        console.warn("Missing fieldset: " + this.item.fieldset)
        return []
      }
    }
  },
  methods: {
    update() {
      this.$emit('field-update', this.item)
    },
    remove(shouldRemove) {
      this.deleteDialog = false
      if (shouldRemove) {
        this.$emit('field-remove', this.item)
      }
    }
  }
}

export default {
  name: "case-config-schema",
  data: () => ({
    dialogApplyCases: false,
    schemaStr: "",
    schemaReady: false,
    newfieldId: -1,
    cmOptionsSchema: {
      tabSize: 4,
      theme: "base16-light",
      lineNumbers: true,
      line: true
    }
  }),
  props: { caseType: { type: Object, required: true } },
  components: { SortableItem, EditCodePane, draggable },
  mounted () {
    if (this.caseType && this.caseType.currentSchema) {
      const schema = yaml.safeDump(this.caseType.currentSchema)
      const schemaLines = schema.split('\n')
      let formattedSchema = ""
      schemaLines.forEach(function(line) { if ((!line.includes("null")) && (!line.includes("__typename:"))) { formattedSchema += line + '\n' } }) // Remove all null values added by GraphQL
      this.schemaStr = formattedSchema
    } else {
      this.schemaStr = ""
    }
  },
  watch: {
    caseType: function() {
      if (this.caseType && this.caseType.currentSchema) {
        const schema = yaml.safeDump(this.caseType.currentSchema)
        const schemaLines = schema.split('\n')
        let formattedSchema = ""
        schemaLines.forEach(function(line) { if ((!line.includes("null")) && (!line.includes("__typename:"))) { formattedSchema += line + '\n' } }) // Remove all null values added by GraphQL
        this.schemaStr = formattedSchema
      } else {
        this.schemaStr = ""
      }
    }
  },
  computed: {
    ...mapState({
      isAccountAdmin: state => state.currentUser && state.currentUser.isAccountAdmin,
      allCaseTypes: state => (state.caseTypes && state.caseTypes.all) ? state.caseTypes.all : []
    }),
    isAppAdmin() {
      return !!this.isAccountAdmin
    },
    schemaCompletions() {
      const types = this.allCaseTypes || []
      const codes = types.map(function (ct) { return ct && ct.code }).filter(Boolean)
      const scripts = (this.caseType && this.caseType.scripts) ? this.caseType.scripts : []
      const scriptNames = scripts.map(function (script) { return script && script.name }).filter(Boolean)
      return {
        caseTypeCodes: codes,
        scriptNames: scriptNames,
        uniqueIdentifierField: this.uniqueIdentifierField || ""
      }
    },
    fieldsets() {
      const commonFieldset = {
        name: "common",
        fields: [
          { name: "title", type: "string" }, { name: "created_at", type: "date" }, { name: "updated_at", type: "date" }, { name: "current_state", type: "string" }
        ]
      }
      let fieldsets = []
      fieldsets.push(commonFieldset)
      if (this.caseType.currentSchema.fieldsets) {
        fieldsets = fieldsets.concat(this.caseType.currentSchema.fieldsets)
        return fieldsets
      } else {
        return []
      }
    },
    displayFields: {
      get() {
        if (this.caseType && this.caseType.displayFields) {
          return this.caseType.displayFields
        } else {
          return []
        }
      },
      set(displayFields) {
        if (this.caseType) {
          this.caseType.displayFields = displayFields
        }
        this.$emit('update-summary-field-data', displayFields)
      }
    },
    uniqueIdentifierField: {
      get() {
        if (this.caseType) {
          return this.caseType.uniqueIdentifierField
        } else {
          return null
        }
      },
      set(schema) {
        this.$emit('update-summary-data', { key: "uniqueIdentifierField", value: schema })
      }
    }
  },
  methods: {
    updateSchema(update) {
      if (this.schemaReady) { // Added to ignore first update caused on creation - keeps save button disabled
        this.schemaStr = update
        this.$emit('update-summary-data', { key: "currentSchema", value: this.schemaStr })
      }
      this.schemaReady = true
    },
    async updateExistingCases() {
      this.dialogApplyCases = false
      this.$store.dispatch("updateExistingCases", this.caseType.id)
    },
    updateDisplayField(displayField) {
      const updatedFields = this.displayFields
      const fieldIds = updatedFields.map(field => field.id)
      const index = fieldIds.indexOf(displayField.id)
      updatedFields[index] = displayField
      this.displayFields = updatedFields
    },
    async addNewDisplayField() {
      if (this.displayFields) {
        const position = this.displayFields.length
        this.displayFields.push({ id: this.newfieldId, fieldset: "", field: "", position: position })
        this.newfieldId--
      }
    },
    async removeDisplayField(field) {
      if (field.id > 0) {
        const response = await this.$store.dispatch("deleteSummaryDisplayField", field.id)
        if (!response.error) {
          const index = this.displayFields.indexOf(field)
          if (index >= 0) this.displayFields.splice(index, 1)
        }
      } else {
        const index = this.displayFields.indexOf(field)
        if (index >= 0) this.displayFields.splice(index, 1)
      }
    }
  }
}
</script>

<style>

.schema-editor-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 50vh;
  max-height: 50vh;
  min-height: 240px;
  overflow: hidden;
}

.schema-editor-shell .monaco-editor-frame,
.schema-editor-shell >>> .monaco-editor-frame {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

.summary-fields .v-list__tile {
  margin-bottom: 8px;
  cursor: pointer;
  padding: 10px 0;
}

.summary-fields .font-awesome-icon {
  cursor: pointer;
}

.summary-fields .font-awesome-icon:hover:active {
  cursor: pointer;
}

.summary-field {
  position: relative;
  font-family: "Roboto", sans-serif !important;
}
</style>
